import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from 'react-i18next';
import FilterSection from '../components/FilterSection';
import FilterChips from '../components/FilterChips';
import AggregatedDashboard from '../components/AggregatedDashboard';
import SourcesDashboard from '../components/SourcesDashboard';
import AnswerSidebar from '../components/AnswerSidebar';
import { CardBox, Button, Badge } from '../components/UI';
import { IconArrowLeft, IconDownload, IconAlertCircle, IconFile, IconEye } from '@tabler/icons-react';

// Shared utility function for engine icons
const getEngineIcon = (engine) => {
  const engineName = engine.toLowerCase();
  const logoMap = {
    'openai': '/logos/chatgpt.png',
    'chatgpt': '/logos/chatgpt.png',
    'gemini': '/logos/gemini.png',
    'perplexity': '/logos/perplexity.png'
  };
  
  const logoSrc = logoMap[engineName];
  if (logoSrc) {
    return (
      <img 
        src={logoSrc} 
        alt={`${engine} logo`}
        className="w-6 h-6 rounded-full object-cover"
      />
    );
  }
  return <div className="w-6 h-6 rounded-full bg-gray-300"></div>;
};

// Rankings View Component
const RankingsView = ({ batchId, clientFilter, setClientFilter, hideZeroCompetitors, setHideZeroCompetitors, sortBy, setSortBy, viewMode }) => {
  const { t, i18n } = useTranslation();
  const { isDark } = useTheme();
  const isRTL = i18n.dir() === 'rtl';
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [activeCardId, setActiveCardId] = useState(null);

  // Create per-engine analyses from individual database records
  const createPerEngineAnalysesFromDB = async (batchId) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5555/api';
      const response = await fetch(`${apiUrl}/search-execution/batches/${batchId}/individual-records`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch individual records');
      }

      const { clientRecords, competitorRecords } = await response.json();

      console.log('Raw API response:');
      console.log('clientRecords:', clientRecords);
      console.log('competitorRecords:', competitorRecords);

      // Group records by query and engine to create analyses
      const analysisMap = new Map();

      // Process client records
      clientRecords.forEach(client => {
        const key = `${client.query}_${client.engine}`;
        if (!analysisMap.has(key)) {
          analysisMap.set(key, {
            query: client.query,
            engine: client.engine,
            clientName: client.client_name,
            clientFound: client.occurrences > 0 || client.first_position !== null,
            clientRank: 1,
            totalEntities: 0,
            competitorsBefore: [],
            competitorsAfter: [],
            allPositions: [],
            answerLength: client.answer_length,
            clientPosition: client.first_position || 'Not Found',
            isClientNotFound: !client.first_position
          });
        }

        const analysis = analysisMap.get(key);
        if (client.first_position) {
          analysis.allPositions.push({
            name: client.client_name,
            type: 'client',
            position: client.first_position,
            positionPercentage: ((client.first_position / client.answer_length) * 100),
            mentions: client.occurrences,
            score: 0
          });
        }
      });

      // Process competitor records
      competitorRecords.forEach(competitor => {
        const key = `${competitor.query}_${competitor.engine}`;
        if (!analysisMap.has(key)) {
          return;
        }

        const analysis = analysisMap.get(key);
        if (competitor.first_position) {
          analysis.allPositions.push({
            name: competitor.competitor_name,
            type: 'competitor',
            position: competitor.first_position,
            positionPercentage: ((competitor.first_position / competitor.answer_length) * 100),
            mentions: competitor.occurrences,
            score: 0
          });
        }
      });

      // Calculate rankings for each analysis
      const analyses = Array.from(analysisMap.values()).map(analysis => {
        analysis.allPositions.sort((a, b) => a.position - b.position);

        const clientIndex = analysis.allPositions.findIndex(e => e.type === 'client');
        const competitorCount = analysis.allPositions.filter(e => e.type === 'competitor').length;

        if (clientIndex !== -1) {
          // Client found - normal ranking
          analysis.clientRank = clientIndex + 1;
          analysis.totalEntities = analysis.allPositions.length;
          analysis.isClientNotFound = false;
          analysis.competitorsBefore = analysis.allPositions.slice(0, clientIndex).filter(e => e.type === 'competitor');
          analysis.competitorsAfter = analysis.allPositions.slice(clientIndex + 1).filter(e => e.type === 'competitor');
        } else {
          // Client not found - special handling
          analysis.clientRank = null; // Will be displayed as "Not Found"
          analysis.totalEntities = competitorCount; // Only count competitors
          analysis.isClientNotFound = true;
          analysis.competitorsBefore = analysis.allPositions.filter(e => e.type === 'competitor');
          analysis.competitorsAfter = [];
        }

        return analysis;
      });

      console.log('Database-based analyses created:', analyses.length);
      console.log('First analysis object:', analyses[0]);
      analyses.forEach(analysis => {
        console.log(`${analysis.query} (${analysis.engine}): Client rank ${analysis.clientRank}/${analysis.totalEntities}, ${analysis.competitorsBefore.length} before + ${analysis.competitorsAfter.length} after`);
        console.log('  - allPositions:', analysis.allPositions);
        console.log('  - competitorsBefore:', analysis.competitorsBefore);
        console.log('  - competitorsAfter:', analysis.competitorsAfter);
      });

      return analyses;

    } catch (error) {
      console.error('Failed to create analyses from database:', error);
      throw error;
    }
  };

  useEffect(() => {
    const loadAnalyses = async () => {
      try {
        setLoading(true);
        const dbAnalyses = await createPerEngineAnalysesFromDB(batchId);
        setAnalyses(dbAnalyses);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (batchId) {
      loadAnalyses();
    }
  }, [batchId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <CardBox className="text-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600 text-sm">{t('results.loading.rankings')}</p>
        </CardBox>
      </div>
    );
  }

  if (error) {
    return (
      <CardBox className="bg-red-50 text-center">
        <div className="flex items-start gap-4">
          <IconAlertCircle size={24} className="text-red-500 mt-1" />
          <div>
            <p className="text-red-600 mb-2">{t('results.error.loadingRankings')}: {error}</p>
            <p className="text-red-500 text-sm">{t('results.error.newSystemRequired')}</p>
          </div>
        </div>
      </CardBox>
    );
  }

  // Apply filters
  let filteredAnalyses = analyses;
  
  // Client visibility filter
  if (clientFilter === 'found') {
    filteredAnalyses = filteredAnalyses.filter(analysis => analysis.clientFound);
  } else if (clientFilter === 'notfound') {
    filteredAnalyses = filteredAnalyses.filter(analysis => !analysis.clientFound);
  }
  
  // Competitors filter
  if (hideZeroCompetitors) {
    filteredAnalyses = filteredAnalyses.filter(
      analysis => analysis.competitorsBefore.length > 0 || analysis.competitorsAfter.length > 0
    );
  }
  
  // Apply sorting
  let sortedAnalyses = [...filteredAnalyses];
  if (sortBy === 'position') {
    sortedAnalyses.sort((a, b) => {
      if (a.isClientNotFound) return 1;
      if (b.isClientNotFound) return -1;
      return a.clientPosition - b.clientPosition;
    });
  } else if (sortBy === 'rank') {
    sortedAnalyses.sort((a, b) => a.clientRank - b.clientRank);
  } else if (sortBy === 'engine') {
    sortedAnalyses.sort((a, b) => a.engine.localeCompare(b.engine));
  }

  console.log('Filtering and sorting results:');
  console.log('  - Total analyses:', analyses.length);
  console.log('  - After filtering:', filteredAnalyses.length);
  console.log('  - After sorting:', sortedAnalyses.length);
  console.log('  - clientFilter:', clientFilter);
  console.log('  - hideZeroCompetitors:', hideZeroCompetitors);
  console.log('  - sortBy:', sortBy);

  const clearAllFilters = () => {
    setClientFilter('all');
    setHideZeroCompetitors(false);
    setSortBy('position');
  };

  // Fetch full answer for a specific query and engine
  const fetchAnswer = async (query, engine) => {
    const cardId = `${query}_${engine}`;
    setActiveCardId(cardId);
    setSelectedAnswer({ loading: true, searchTerm: query, engine });
    setSidebarOpen(true);

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5555/api';
      const response = await fetch(
        `${apiUrl}/search-execution/batches/${batchId}/execution-by-query?` +
        `query=${encodeURIComponent(query)}&engine=${encodeURIComponent(engine)}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch answer');
      }

      const data = await response.json();

      setSelectedAnswer({
        searchTerm: data.searchTerm,
        engine: data.engine,
        textAnswer: data.engineResult?.textAnswer || '',
        citations: data.engineResult?.citations || [],
        status: data.status,
        executionTime: data.executionTime,
        executedAt: data.executedAt
      });
    } catch (error) {
      console.error('Failed to fetch answer:', error);
      setSelectedAnswer({
        searchTerm: query,
        engine,
        error: t('results.answerSidebar.noAnswer')
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <CardBox>
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900 dark:text-[#f5f5f5]">{t('results.rankings.title')}</h2>
          <Badge color="info">
            {t('results.rankings.dataSource')}
          </Badge>
        </div>
      </CardBox>

      {/* Filter Section */}
      <FilterSection
        clientFilter={clientFilter}
        setClientFilter={setClientFilter}
        hideZeroCompetitors={hideZeroCompetitors}
        setHideZeroCompetitors={setHideZeroCompetitors}
        sortBy={sortBy}
        setSortBy={setSortBy}
        filteredCount={sortedAnalyses.length}
        totalCount={analyses.length}
        onClearFilters={clearAllFilters}
        hideSearch={true}
        viewMode={viewMode}
      />

      {/* Active Filter Chips */}
      <FilterChips
        clientFilter={clientFilter}
        setClientFilter={setClientFilter}
        hideZeroCompetitors={hideZeroCompetitors}
        setHideZeroCompetitors={setHideZeroCompetitors}
        sortBy={sortBy}
        setSortBy={setSortBy}
        onClearAll={clearAllFilters}
        hideSearch={true}
      />
      
      {/* Statistics Panel */}
      <CardBox title={t('results.rankings.analysisOverview')}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gray-50 dark:bg-[#262626] rounded-xl p-4">
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-300">{analyses.length}</div>
            <div className="text-sm text-gray-600 dark:text-[#c4c4c4]">{t('results.rankings.totalCombinations')}</div>
          </div>
          <div className="bg-gray-50 dark:bg-[#262626] rounded-xl p-4">
            <div className="text-2xl font-bold text-green-600 dark:text-green-300">
              {analyses.filter(a => a.competitorsBefore.length > 0 || a.competitorsAfter.length > 0).length}
            </div>
            <div className="text-sm text-gray-600 dark:text-[#c4c4c4]">{t('results.rankings.withCompetitors')}</div>
          </div>
          <div className="bg-gray-50 dark:bg-[#262626] rounded-xl p-4">
            <div className="text-2xl font-bold text-purple-600 dark:text-purple-300">{sortedAnalyses.length}</div>
            <div className="text-sm text-gray-600 dark:text-[#c4c4c4]">{t('results.rankings.filteredResults')}</div>
          </div>
          <div className="bg-gray-50 dark:bg-[#262626] rounded-xl p-4">
            <div className="text-sm font-medium text-gray-700 dark:text-[#c4c4c4]">{t('results.rankings.dataSourceLabel')}</div>
            <div className="text-xs text-gray-500 dark:text-[#999999]">{t('results.rankings.individualRecords')}</div>
          </div>
        </div>
      </CardBox>

      {/* Results */}
      {sortedAnalyses.length === 0 ? (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-xl p-8 text-center shadow-sm">
          <svg className="mx-auto h-12 w-12 text-yellow-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h3 className="text-lg font-medium text-yellow-800 dark:text-yellow-200 mb-2">{t('results.rankings.noResults')}</h3>
          <p className="text-sm text-yellow-700 dark:text-yellow-200/80 mb-4">
            {t('results.rankings.noResultsHint', { count: analyses.length })}
          </p>
          <button
            onClick={clearAllFilters}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          >
            {t('results.rankings.clearFilters')}
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {console.log('About to render sortedAnalyses:', sortedAnalyses)}
          {sortedAnalyses.map((analysis, index) => {
            console.log('Rendering analysis:', index, analysis);
            const uniqueKey = `ranking-${index}`;
            const cardId = `${analysis.query}_${analysis.engine}`;
            const isActive = activeCardId === cardId;

            return (
              <div
                key={uniqueKey}
                style={{
                  backgroundColor: isActive
                    ? (isDark ? 'rgba(34, 197, 94, 0.18)' : '#f0fdf4')
                    : (isDark ? '#1f1f1f' : '#ffffff'),
                  border: isActive
                    ? '2px solid #16a34a'
                    : '1px solid ' + (isDark ? '#2e2e2e' : '#e5e7eb'),
                  borderRadius: '12px',
                  padding: '20px',
                  boxShadow: isActive
                    ? '0 0 20px rgba(22, 163, 74, 0.2), 0 4px 6px rgba(0, 0, 0, 0.1)'
                    : '0 1px 3px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.3s ease'
                }}
              >
                {/* Header Section */}
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                    <h3 style={{ fontSize: '18px', fontWeight: '600', color: isDark ? '#f5f5f5' : '#111827' }}>
                      {analysis.query || 'No Query'}
                    </h3>
                    <button
                      onClick={() => fetchAnswer(analysis.query, analysis.engine)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '6px 12px',
                        backgroundColor: '#d8f9b8',
                        color: '#374151',
                        border: '1px solid #bbeb9a',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#c5f0a0';
                        e.target.style.borderColor = '#a8e285';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = '#d8f9b8';
                        e.target.style.borderColor = '#bbeb9a';
                      }}
                    >
                      <IconEye size={16} />
                      {t('results.rankings.viewAnswer')}
                    </button>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {getEngineIcon(analysis.engine || 'unknown')}
                      <span style={{ fontSize: '14px', color: isDark ? '#c4c4c4' : '#4b5563' }}>
                        {analysis.engine === 'openai' ? 'ChatGPT' :
                         analysis.engine ? analysis.engine.charAt(0).toUpperCase() + analysis.engine.slice(1) : 'Unknown'}
                      </span>
                    </div>
                    <span style={{ fontSize: '14px', color: isDark ? '#999999' : '#6b7280' }}>
                      {analysis.answerLength?.toLocaleString() || '0'} characters
                    </span>
                  </div>
                </div>

                {/* Main Content Grid */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px' }}>
                  {/* Left Column - Metrics */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {/* Client Ranking */}
                    <div style={{
                      backgroundColor: isDark ? 'rgba(59, 130, 246, 0.12)' : '#eff6ff',
                      border: '1px solid ' + (isDark ? 'rgba(59, 130, 246, 0.35)' : '#bfdbfe'),
                      borderRadius: '8px',
                      padding: '12px'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '14px', color: isDark ? '#c4c4c4' : '#4b5563' }}>Client Ranking</span>
                        <span style={{ fontSize: '24px', fontWeight: '700' }}>
                          {analysis.isClientNotFound ? (
                            <span style={{ color: '#ef4444' }}>Not Found</span>
                          ) : (
                            <span style={{ color: '#2563eb' }}>#{analysis.clientRank}</span>
                          )}
                        </span>
                      </div>
                      {!analysis.isClientNotFound && (
                        <div style={{ fontSize: '12px', color: isDark ? '#999999' : '#6b7280', marginTop: '4px' }}>
                          out of {analysis.totalEntities} entities
                        </div>
                      )}
                    </div>

                    {/* Competitors Before */}
                    <div style={{
                      backgroundColor: isDark ? 'rgba(239, 68, 68, 0.12)' : '#fef2f2',
                      border: '1px solid ' + (isDark ? 'rgba(239, 68, 68, 0.35)' : '#fecaca'),
                      borderRadius: '8px',
                      padding: '12px'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '14px', color: isDark ? '#c4c4c4' : '#4b5563' }}>Competitors Before</span>
                        <span style={{ fontSize: '20px', fontWeight: '700', color: '#dc2626' }}>
                          {analysis.competitorsBefore?.length || 0}
                        </span>
                      </div>
                      {analysis.competitorsBefore && analysis.competitorsBefore.length > 0 && (
                        <div style={{ fontSize: '12px', color: isDark ? '#999999' : '#6b7280', marginTop: '8px' }}>
                          {analysis.competitorsBefore.slice(0, 3).map(c => c.name).join(', ')}
                          {analysis.competitorsBefore.length > 3 && ' ...'}
                        </div>
                      )}
                    </div>

                    {/* Competitors After */}
                    <div style={{
                      backgroundColor: isDark ? 'rgba(34, 197, 94, 0.12)' : '#f0fdf4',
                      border: '1px solid ' + (isDark ? 'rgba(34, 197, 94, 0.35)' : '#bbf7d0'),
                      borderRadius: '8px',
                      padding: '12px'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '14px', color: isDark ? '#c4c4c4' : '#4b5563' }}>Competitors After</span>
                        <span style={{ fontSize: '20px', fontWeight: '700', color: '#16a34a' }}>
                          {analysis.competitorsAfter?.length || 0}
                        </span>
                      </div>
                      {analysis.competitorsAfter && analysis.competitorsAfter.length > 0 && (
                        <div style={{ fontSize: '12px', color: isDark ? '#999999' : '#6b7280', marginTop: '8px' }}>
                          {analysis.competitorsAfter.slice(0, 3).map(c => c.name).join(', ')}
                          {analysis.competitorsAfter.length > 3 && ' ...'}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right Column - Entity List */}
                  <div style={{
                    backgroundColor: isDark ? '#262626' : '#f9fafb',
                    border: '1px solid ' + (isDark ? '#2e2e2e' : '#e5e7eb'),
                    borderRadius: '8px',
                    padding: '12px'
                  }}>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: isDark ? '#c4c4c4' : '#374151', marginBottom: '12px' }}>
                      All Entities
                    </div>
                    <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                      {Array.isArray(analysis.allPositions) && analysis.allPositions.length > 0 ? (
                        analysis.allPositions.map((entity, idx) => (
                          <div
                            key={idx}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '12px',
                              padding: '8px',
                              borderRadius: '6px',
                              marginBottom: '4px',
                              backgroundColor: entity.type === 'client'
                                ? (isDark ? 'rgba(59, 130, 246, 0.18)' : '#dbeafe')
                                : (isDark ? '#1f1f1f' : '#ffffff'),
                              border: entity.type === 'client'
                                ? '1px solid ' + (isDark ? 'rgba(59, 130, 246, 0.4)' : '#93c5fd')
                                : '1px solid ' + (isDark ? '#2e2e2e' : '#e5e7eb'),
                              fontWeight: entity.type === 'client' ? '600' : '400'
                            }}
                          >
                            <span style={{ fontSize: '12px', color: isDark ? '#999999' : '#6b7280', width: '28px' }}>
                              #{idx + 1}
                            </span>
                            <span style={{ fontSize: '14px', flex: 1, color: entity.type === 'client' ? (isDark ? '#bfdbfe' : '#1e40af') : (isDark ? '#c4c4c4' : '#374151') }}>
                              {entity.name}
                            </span>
                            <span style={{ fontSize: '12px', color: isDark ? '#999999' : '#9ca3af' }}>
                              {entity.position ? `${((entity.position / analysis.answerLength) * 100).toFixed(0)}%` : '-'}
                            </span>
                          </div>
                        ))
                      ) : (
                        <div style={{ textAlign: 'center', color: isDark ? '#999999' : '#6b7280', padding: '24px' }}>
                          {analysis.isClientNotFound
                            ? 'Client not found in results'
                            : 'No entities found'}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Answer Sidebar */}
      <AnswerSidebar
        isOpen={sidebarOpen}
        onClose={() => {
          setSidebarOpen(false);
          setActiveCardId(null);
        }}
        answer={selectedAnswer}
      />
    </div>
  );
};

const ResultsDashboard = () => {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isDark } = useTheme();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'he';
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedView, setSelectedView] = useState('dashboard'); // 'dashboard', 'rankings', 'sources'
  const [searchSources, setSearchSources] = useState([]);
  const [viewMode, setViewMode] = useState(() => {
    // Load saved preference or default to 'detailed'
    return localStorage.getItem('resultsViewMode') || 'detailed';
  });
  
  // Rankings view filters and sorting (like original rankflow)
  const [hideZeroCompetitors, setHideZeroCompetitors] = useState(false);
  const [sortBy, setSortBy] = useState('position'); // 'default', 'position', 'engine'
  const [clientFilter, setClientFilter] = useState('all'); // 'all', 'found', 'notfound'

  useEffect(() => {
    if (batchId && user) {
      loadResults();
    }
  }, [batchId, user]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadResults = async () => {
    try {
      setLoading(true);
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5555/api';
      
      // Load batch status, execution results, and search sources
      const [batchResponse, executionsResponse, sourcesResponse] = await Promise.all([
        fetch(`${apiUrl}/search-execution/batches/${batchId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          }
        }),
        fetch(`${apiUrl}/search-execution/batches/${batchId}/results`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          }
        }),
        fetch(`${apiUrl}/search-execution/batches/${batchId}/individual-records`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          }
        })
      ]);

      if (batchResponse.ok && executionsResponse.ok) {
        const batchData = await batchResponse.json();
        const executionsData = await executionsResponse.json();
        
        setResults({
          batch: batchData.batch,
          executions: executionsData.executions || []
        });
        
        // Also load search sources if available
        if (sourcesResponse.ok) {
          const sourcesData = await sourcesResponse.json();
          setSearchSources(sourcesData.searchSources || []);
        }
      } else {
        throw new Error('Failed to load results');
      }
    } catch (error) {
      console.error('Failed to load results:', error);
      setError(t('results.error.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const toggleViewMode = () => {
    const newMode = viewMode === 'detailed' ? 'simple' : 'detailed';
    setViewMode(newMode);
    localStorage.setItem('resultsViewMode', newMode);
  };





  const formatExecutionTime = (ms) => {
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };



  const exportResults = (format) => {
    // Basic CSV export functionality
    if (format === 'csv' && results?.executions) {
      const headers = [
        t('results.export.searchTerm'),
        t('results.export.status'),
        t('results.export.clientScore'),
        t('results.export.clientMentions'),
        t('results.export.competitorsFound'),
        t('results.export.executionTime'),
        t('results.export.chatGPTStatus'),
        t('results.export.geminiStatus'),
        t('results.export.perplexityStatus')
      ];
      const rows = results.executions.map(execution => [
        execution.searchTerm,
        execution.status,
        execution.ai_analysis_results?.summary?.overallClientScore || 0,
        execution.ai_analysis_results?.summary?.clientMentions || 0,
        execution.ai_analysis_results?.summary?.topCompetitors?.length || 0,
        execution.executionTime ? formatExecutionTime(execution.executionTime) : 'N/A',
        execution.results?.engines?.openai?.status || 'N/A',
        execution.results?.engines?.gemini?.status || 'N/A',
        execution.results?.engines?.perplexity?.status || 'N/A'
      ]);
      
      const csvContent = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `search-results-${results.batch.name.replace(/[^a-zA-Z0-9]/g, '-')}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  if (!user) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8 pt-32">
        <CardBox className="text-center">
          <h1 className="text-xl font-bold mb-4 text-gray-800">{t('results.signInRequired')}</h1>
          <p className="text-sm text-gray-600">{t('results.signInMessage')}</p>
        </CardBox>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8 pt-32">
        <CardBox className="text-center">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mr-3"></div>
            <span className="text-sm text-gray-600">{t('results.loading.results')}</span>
          </div>
        </CardBox>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8 pt-32">
        <CardBox className="text-center">
          <IconAlertCircle size={48} className="mx-auto text-red-400 mb-4" />
          <h1 className="text-xl font-bold mb-2 text-gray-800">{t('results.error.title')}</h1>
          <p className="text-sm mb-6 text-gray-600">{error}</p>
          <Button
            onClick={() => navigate('/search')}
            leftIcon={<IconArrowLeft size={18} />}
          >
            {t('results.backToSearch')}
          </Button>
        </CardBox>
      </div>
    );
  }

  if (!results) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8 pt-32">
        <CardBox className="text-center">
          <IconFile size={48} className="mx-auto text-gray-400 mb-4" />
          <h1 className="text-xl font-bold mb-4 text-gray-800">{t('results.notFound.title')}</h1>
          <p className="text-sm mb-6 text-gray-600">{t('results.notFound.message')}</p>
          <Button
            onClick={() => navigate('/search')}
            leftIcon={<IconArrowLeft size={18} />}
          >
            {t('results.backToSearch')}
          </Button>
        </CardBox>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#171717]">
      {/* Full Width Header */}
      <div className="bg-white dark:bg-[#1f1f1f] border-b border-gray-200 dark:border-[#2e2e2e] shadow-sm">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={() => navigate('/search')}
              className="text-gray-600 hover:text-gray-900 transition-colors flex items-center gap-2"
            >
              <svg className={`w-5 h-5 ${isRTL ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="text-sm">{t('results.backToSearch')}</span>
            </button>
            <div className="flex items-center gap-2">
              <button
                onClick={toggleViewMode}
                className={`px-3 py-1.5 rounded text-sm transition-all ${
                  viewMode === 'detailed'
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-200'
                    : 'text-gray-600 hover:bg-gray-100 dark:text-[#c4c4c4] dark:hover:bg-[#262626]'
                }`}
              >
                {t('results.viewMode.detailed')}
              </button>
              <button
                onClick={toggleViewMode}
                className={`px-3 py-1.5 rounded text-sm transition-all ${
                  viewMode === 'simple'
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-200'
                    : 'text-gray-600 hover:bg-gray-100 dark:text-[#c4c4c4] dark:hover:bg-[#262626]'
                }`}
              >
                {t('results.viewMode.simple')}
              </button>
            </div>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-[#f5f5f5] mb-1">
              {t('results.title')}
            </h1>
            <p className="text-gray-600 dark:text-[#c4c4c4]">{results.batch.name}</p>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white dark:bg-[#1f1f1f] border-b border-gray-200 dark:border-[#2e2e2e]">
        <div className="px-6">
          <div className="flex items-center justify-between">
            <nav className="flex gap-8">
              <button
                onClick={() => setSelectedView('dashboard')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  selectedView === 'dashboard'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-300'
                    : 'border-transparent text-gray-500 dark:text-[#999999] hover:text-gray-700 dark:hover:text-[#c4c4c4] hover:border-gray-300 dark:hover:border-[#3a3a3a]'
                }`}
              >
                {t('results.tabs.dashboard')}
              </button>
              <button
                onClick={() => setSelectedView('rankings')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  selectedView === 'rankings'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-300'
                    : 'border-transparent text-gray-500 dark:text-[#999999] hover:text-gray-700 dark:hover:text-[#c4c4c4] hover:border-gray-300 dark:hover:border-[#3a3a3a]'
                }`}
              >
                {t('results.tabs.rankings')}
              </button>
              <button
                onClick={() => setSelectedView('sources')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  selectedView === 'sources'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-300'
                    : 'border-transparent text-gray-500 dark:text-[#999999] hover:text-gray-700 dark:hover:text-[#c4c4c4] hover:border-gray-300 dark:hover:border-[#3a3a3a]'
                }`}
              >
                {t('results.tabs.sources')}
              </button>
            </nav>
            <Button
              onClick={() => exportResults('csv')}
              leftIcon={<IconDownload size={18} />}
              className="my-3"
            >
              {t('results.export.button')}
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="px-6 py-8">
        {results.executions.length === 0 ? (
          <div className="text-center py-16 bg-white dark:bg-[#1f1f1f] rounded-lg">
            <svg className="mx-auto h-12 w-12 text-gray-400 dark:text-[#999999] mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 dark:text-[#f5f5f5] mb-2">{t('results.noResultsAvailable.title')}</h3>
            <p className="text-gray-500 dark:text-[#999999]">{t('results.noResultsAvailable.message')}</p>
          </div>
        ) : selectedView === 'dashboard' ? (
          // Dashboard View - Aggregated analytics
          <div>
            <AggregatedDashboard executions={results.executions} searchSources={searchSources} viewMode={viewMode} />
          </div>
        ) : selectedView === 'rankings' ? (
          // Rankings View - Individual query/engine analysis (like original rankflow)
          <RankingsView batchId={batchId}
                       clientFilter={clientFilter}
                       setClientFilter={setClientFilter}
                       hideZeroCompetitors={hideZeroCompetitors}
                       setHideZeroCompetitors={setHideZeroCompetitors}
                       sortBy={sortBy}
                       setSortBy={setSortBy}
                       viewMode={viewMode} />
        ) : selectedView === 'sources' ? (
          // Sources View - Use the new SourcesDashboard component
          <SourcesDashboard
            searchSources={searchSources}
            viewMode={viewMode}
            isGlobal={false}
          />
        ) : null}
      </div>
    </div>
  );
};

export default ResultsDashboard;
